import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { crewOpsApi } from "@/api";
import { Zap, RefreshCw, AlertTriangle, CheckCircle, ExternalLink, Download, X, FileText, FileSpreadsheet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { exportPDF, exportCSV, todayStr } from "@/utils/exportUtils";

const DUTY_COLORS = {
  flight:          { bg: "bg-blue-600",   text: "text-white", label: "FLT" },
  reserve:         { bg: "bg-amber-600",  text: "text-white", label: "RSV" },
  training:        { bg: "bg-purple-600", text: "text-white", label: "TRN" },
  leave:           { bg: "bg-slate-600",  text: "text-white", label: "LVE" },
  standby:         { bg: "bg-yellow-600", text: "text-white", label: "SBY" },
  disruption_cover:{ bg: "bg-green-600",  text: "text-white", label: "DIS" },
  disrupted:       { bg: "bg-red-800",    text: "text-white", label: "DIS!" },
  cancelled:       { bg: "bg-zinc-700",   text: "text-zinc-400", label: "CNX" },
};

// Tooltip: uses onMouseMove on the table — no RAF loop, no fixed-pos issues
function Tooltip({ tooltip, x, y }) {
  if (!tooltip) return null;
  const left = Math.min(x + 16, window.innerWidth - 300);
  const top  = y + 24 + 160 > window.innerHeight ? y - 160 : y + 24;
  const a = tooltip.assignment;
  return createPortal(
    <div style={{
      position: "fixed", left, top, zIndex: 9999,
      background: "#111827", border: "1px solid #1f2d4a",
      borderRadius: "4px", padding: "10px 13px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
      pointerEvents: "none", minWidth: "220px", maxWidth: "290px",
    }}>
      <div style={{ fontWeight: 700, color: "#e2e8f0", marginBottom: 4, fontSize: 12 }}>
        {a.pairing_code || a.leave_type || a.assignment_type}
      </div>
      <div style={{ color: "#94a3b8", fontSize: 11 }}>{a.start_date} → {a.end_date}</div>
      <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2, textTransform: "capitalize" }}>
        {a.assignment_type?.replace("_", " ")} · {a.rank_slot}
      </div>
      {a.disruption_title && (
        <div style={{ color: "#f97316", fontSize: 11, marginTop: 4, borderTop: "1px solid #1f2d4a", paddingTop: 4 }}>
          Disruption: {a.disruption_title}
        </div>
      )}
      {a.delay_minutes && (
        <div style={{ color: "#fbbf24", fontSize: 11, marginTop: 2 }}>+{a.delay_minutes} min delay</div>
      )}
      {tooltip.pairingInfo && (
        <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 2 }}>{tooltip.pairingInfo.pairing_notes}</div>
      )}
      <div style={{ color: "#64748b", fontSize: 10, marginTop: 3 }}>
        {a.source?.replace(/_/g, " ")}
      </div>
    </div>,
    document.body
  );
}

function buildAssignmentMap(assignments) {
  const map = {};
  assignments.forEach(a => {
    if (!map[a.crew_member_id]) map[a.crew_member_id] = {};
    const start = new Date(a.start_date + "T00:00:00Z");
    const end = new Date(a.end_date + "T00:00:00Z");
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.getUTCDate();
      if (day >= 1 && day <= 30) {
        map[a.crew_member_id][day] = a;
      }
    }
  });
  return map;
}

function buildViolationMap(violations) {
  const map = {};
  violations.forEach(v => {
    if (!map[v.crew_member_id]) map[v.crew_member_id] = [];
    map[v.crew_member_id].push(v);
  });
  return map;
}

const DAYS = Array.from({ length: 30 }, (_, i) => i + 1);

const RANK_ORDER = ["Captain", "First Officer", "Purser", "Senior Cabin Crew"];

export default function RosterPlanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [rosterId, setRosterId] = useState(null);
  const [assignResult, setAssignResult] = useState(null);
  const [filterRank, setFilterRank] = useState("");
  const [error, setError] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rosters = await crewOpsApi.getRosters();
      if (!rosters.data?.length) { setLoading(false); return; }
      const firstRoster = rosters.data[0];
      setRosterId(firstRoster.id);
      const full = await crewOpsApi.getRosterFull(firstRoster.id);
      setData(full.data);
    } catch (err) {
      setError("Failed to load roster. Please refresh the page.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  // Set export date range defaults once roster data loads
  useEffect(() => {
    if (data?.roster?.start_date && !exportFrom) {
      setExportFrom(data.roster.start_date);
      setExportTo(data.roster.end_date || data.roster.start_date);
    }
  }, [data]);

  const handleAutoAssign = async () => {
    if (!rosterId) return;
    setAssigning(true);
    setAssignResult(null);
    setError(null);
    try {
      const r = await crewOpsApi.autoAssign(rosterId);
      setAssignResult(r.data);
      await fetchRoster();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Auto-assign failed. Please refresh and try again.";
      setError(msg);
    } finally { setAssigning(false); }
  };

  if (loading) return (
    <div className="flex-1 p-6 flex items-center justify-center h-full">
      <div className="text-primary font-mono text-sm animate-pulse">LOADING ROSTER DATA...</div>
    </div>
  );
  if (!data) return <div className="p-6 text-muted-foreground">No roster data found.</div>;

  const { roster, assignments, crew, pairings, violations } = data;
  const assignmentMap = buildAssignmentMap(assignments);
  const violationMap = buildViolationMap(violations);

  // Compute day labels dynamically from the roster's start date
  const [_rYear, _rMonth] = (roster?.start_date || "")
    .split("-").map(Number);
  const _rY = _rYear || new Date().getFullYear();
  const _rM = _rMonth ? _rMonth - 1 : new Date().getMonth(); // JS 0-indexed
  const DAY_LABELS = DAYS.map(d => {
    const dt = new Date(_rY, _rM, d);
    return { day: d, label: d, weekday: ["Su","Mo","Tu","We","Th","Fr","Sa"][dt.getDay()], isWeekend: dt.getDay() === 0 || dt.getDay() === 6 };
  });

  const filteredCrew = [...crew]
    .filter(c => !filterRank || c.rank === filterRank)
    .sort((a, b) => RANK_ORDER.indexOf(a.rank) - RANK_ORDER.indexOf(b.rank));

  const assigned = assignments.filter(a => a.assignment_type === "flight").length;
  const unassigned = Object.values(pairings || {}).filter(p => p.status === "unassigned").length;
  const violationCount = violations?.length || 0;

  // ── Export helpers ────────────────────────────────────────────────────────
  function buildRosterRows() {
    const rows = [];
    const fromD = new Date(exportFrom + "T00:00:00Z");
    const toD   = new Date(exportTo   + "T00:00:00Z");
    for (const c of crew) {
      const crewAsgn = assignments.filter(a => {
        if (a.crew_member_id !== c.id) return false;
        const sd = new Date(a.start_date + "T00:00:00Z");
        const ed = new Date(a.end_date   + "T00:00:00Z");
        return sd <= toD && ed >= fromD;
      });
      if (!crewAsgn.length) {
        rows.push({ crew_name: c.name, rank: c.rank, base: c.base, employee_id: c.employee_id, date: exportFrom + " – " + exportTo, type: "No assignments in range", pairing: "—", route: "—", fdp: "—" });
        continue;
      }
      for (const a of crewAsgn) {
        const p = pairings?.[a.pairing_id];
        rows.push({
          crew_name: c.name, rank: c.rank, base: c.base, employee_id: c.employee_id,
          date: `${a.start_date} → ${a.end_date}`,
          type: (a.assignment_type || "").replace(/_/g, " ").toUpperCase(),
          pairing: a.pairing_code || a.pairing_id || "—",
          route: p?.routes?.join(", ") || "—",
          fdp: p?.total_fdp_hours ? `${p.total_fdp_hours}h` : "—",
          status: (a.status || "active").toUpperCase(),
        });
      }
    }
    return rows;
  }

  function handleExportPDF() {
    exportPDF({
      title: "Crew Roster",
      subtitle: `Period: ${exportFrom} to ${exportTo} · ${crew.length} crew · Generated ${todayStr()}`,
      columns: [
        { header: "Crew",       key: "crew_name",    width: 38 },
        { header: "Rank",       key: "rank",         width: 20 },
        { header: "Base",       key: "base",         width: 12 },
        { header: "Emp. ID",    key: "employee_id",  width: 18 },
        { header: "Date(s)",    key: "date",         width: 36 },
        { header: "Type",       key: "type",         width: 22 },
        { header: "Pairing",    key: "pairing",      width: 20 },
        { header: "Route",      key: "route",        width: 28 },
        { header: "FDP",        key: "fdp",          width: 14 },
        { header: "Status",     key: "status",       width: 18 },
      ],
      rows: buildRosterRows(),
      filename: `roster-${exportFrom}-${exportTo}.pdf`,
      orientation: "landscape",
    });
    setShowExport(false);
  }

  function handleExportCSV() {
    exportCSV({
      headers: [
        { label: "Crew Name",   key: "crew_name" },
        { label: "Rank",        key: "rank" },
        { label: "Base",        key: "base" },
        { label: "Employee ID", key: "employee_id" },
        { label: "Date(s)",     key: "date" },
        { label: "Type",        key: "type" },
        { label: "Pairing",     key: "pairing" },
        { label: "Route",       key: "route" },
        { label: "FDP (h)",     key: "fdp" },
        { label: "Status",      key: "status" },
      ],
      rows: buildRosterRows(),
      filename: `roster-${exportFrom}-${exportTo}.csv`,
    });
    setShowExport(false);
  }

  return (
    <div className="p-4 space-y-4 fade-in-up" data-testid="roster-planner">
      {/* Export Modal */}
      {showExport && createPortal(
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setShowExport(false)}>
          <div className="bg-card border border-border rounded-sm p-6 w-96 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-bold text-foreground">Export Roster</div>
              <button onClick={() => setShowExport(false)} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
            </div>
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block font-bold uppercase tracking-wide">From Date</label>
                <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)}
                  className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="export-date-from" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block font-bold uppercase tracking-wide">To Date</label>
                <input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)}
                  className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="export-date-to" />
              </div>
              <div className="text-xs text-muted-foreground pt-1">
                Exports assignments for all <span className="text-foreground font-bold">{crew.length} crew members</span> within the selected date range.
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleExportPDF}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground rounded-sm text-sm font-bold hover:bg-primary/90 transition-colors"
                data-testid="roster-export-pdf">
                <FileText size={14} /> Export PDF
              </button>
              <button onClick={handleExportCSV}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-secondary border border-border text-foreground rounded-sm text-sm font-bold hover:bg-secondary/80 transition-colors"
                data-testid="roster-export-csv">
                <FileSpreadsheet size={14} /> Export CSV
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Crew Rostering · Core Module</div>
          <h1 className="text-2xl font-black font-heading text-foreground">
            Roster Planner — <span className="text-primary">{roster?.name}</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <select value={filterRank} onChange={e => setFilterRank(e.target.value)}
            className="bg-card border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
            <option value="">All Ranks</option>
            {RANK_ORDER.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={() => setShowExport(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-secondary border border-border text-foreground rounded-sm text-sm font-semibold hover:bg-secondary/80 transition-colors"
            data-testid="roster-export-button">
            <Download size={13} /> Export Roster
          </button>
          <button onClick={handleAutoAssign} disabled={assigning || unassigned === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
            data-testid="auto-assign-button">
            <Zap size={14} className={assigning ? "animate-pulse" : ""} />
            {assigning ? "Optimising..." : `Auto-Assign (${unassigned} pairings)`}
          </button>
          <button onClick={fetchRoster} className="p-2 border border-border rounded-sm text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Auto-assign result */}
      {assignResult && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-sm text-xs text-green-400" data-testid="auto-assign-result">
          <CheckCircle size={12} /> {assignResult.message}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-sm text-xs text-red-400" data-testid="roster-error-banner">
          <AlertTriangle size={12} /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-muted-foreground hover:text-foreground">✕</button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-3">
        {[
          ["Roster Period", roster?.bid_period || "—", "text-primary"],
          ["Total Crew", filteredCrew.length, "text-foreground"],
          ["Assignments", assigned, "text-blue-400"],
          ["Compliance Violations", violationCount, violationCount > 0 ? "text-red-400" : "text-green-400"],
          ["Status", roster?.roster_status?.toUpperCase(), "text-amber-400"],
        ].map(([lbl, val, col]) => (
          <div key={lbl} className="bg-card border border-border rounded-sm px-3 py-2.5" data-testid={`roster-kpi-${lbl.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">{lbl}</div>
            <div className={`text-sm font-mono font-medium ${col}`}>{val}</div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex gap-3 flex-wrap items-center">
        {Object.entries(DUTY_COLORS).map(([type, cfg]) => (
          <div key={type} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={`inline-block w-5 h-3 rounded-sm ${cfg.bg}`} />
            {cfg.label} = {type.replace("_", " ")}
          </div>
        ))}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block w-5 h-3 rounded-sm bg-muted/20 border border-dashed border-border" />
          OFF = Day Off
        </div>
        <div className="flex items-center gap-1.5 text-xs text-red-400 ml-4">
          <AlertTriangle size={11} /> = Compliance Violation
        </div>
        <button
          onClick={() => navigate("/roster/disruption")}
          className="ml-auto flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          data-testid="goto-disruption-btn">
          <ExternalLink size={11} /> Disruption Console
        </button>
      </div>

      {/* Roster Gantt Grid */}
      <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="roster-grid">
        <div className="overflow-x-auto"
          onMouseMove={e => { if (tooltip) setTooltipPos({ x: e.clientX, y: e.clientY }); }}>
          <table className="text-xs border-collapse" style={{ minWidth: "1400px" }}>
            {/* Day Header */}
            <thead>
              <tr className="border-b border-border bg-background/80">
                <th className="sticky left-0 z-20 bg-background/90 text-left px-3 py-2 text-muted-foreground font-bold tracking-widest uppercase text-xs border-r border-border min-w-[160px]">
                  Crew Member
                </th>
                {DAY_LABELS.map(({ day, label, weekday, isWeekend }) => (
                  <th key={day} className={`px-0.5 py-1 text-center font-bold min-w-[32px] ${isWeekend ? "text-blue-400" : "text-muted-foreground"}`}>
                    <div className="text-xs">{weekday}</div>
                    <div className="text-xs font-mono">{label}</div>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Crew Rows */}
            <tbody>
              {filteredCrew.map((c, idx) => {
                const hasViolation = violationMap[c.id]?.length > 0;
                return (
                  <tr key={c.id} className={`border-b border-border/40 ${idx % 2 === 0 ? "" : "bg-background/30"} ${hasViolation ? "bg-red-500/3" : ""}`}
                    data-testid={`roster-row-${c.employee_id}`}>
                    {/* Crew name (sticky) */}
                    <td className={`sticky left-0 z-10 border-r border-border px-2 py-1.5 ${idx % 2 === 0 ? "bg-card" : "bg-background/50"}`}>
                      <div className="flex items-center gap-1.5">
                        {hasViolation && <AlertTriangle size={10} className="text-red-400 shrink-0" />}
                        <div>
                          <div className="font-medium text-foreground text-xs truncate max-w-[130px]">{c.name}</div>
                          <div className="text-muted-foreground font-mono text-xs">{c.employee_id} · {c.rank.substring(0,3).toUpperCase()}</div>
                        </div>
                      </div>
                    </td>

                    {/* Day cells */}
                    {DAY_LABELS.map(({ day, isWeekend }) => {
                      const assignment = assignmentMap[c.id]?.[day];
                      // Determine display type (cancelled/disrupted override normal type)
                      const displayType = assignment
                        ? (assignment.status === "cancelled" ? "cancelled"
                          : assignment.assignment_type === "disruption_cover" ? "disruption_cover"
                          : assignment.status === "disrupted" ? "disrupted"
                          : assignment.assignment_type)
                        : null;
                      const cfg = displayType ? (DUTY_COLORS[displayType] || DUTY_COLORS.flight) : null;
                      const hasDisruption = assignment?.disruption_id;

                      return (
                        <td key={day}
                          className={`px-0.5 py-1 text-center ${isWeekend ? "bg-blue-500/3" : ""}`}
                          onMouseEnter={e => {
                            if (assignment) {
                              setTooltipPos({ x: e.clientX, y: e.clientY });
                              setTooltip({ assignment, pairingInfo: pairings[assignment.pairing_id] });
                            }
                          }}
                          onMouseLeave={() => setTooltip(null)}>
                          {assignment ? (
                            <span
                              className={`inline-block w-full rounded-sm font-bold py-0.5 cursor-pointer ${cfg.bg} ${cfg.text} ${hasDisruption && displayType !== "disruption_cover" ? "ring-1 ring-orange-500/60" : ""}`}
                              style={{ fontSize: "9px" }}>
                              {cfg.label}
                            </span>
                          ) : (
                            <span className="inline-block w-full py-0.5" style={{ fontSize: "9px" }}>&nbsp;</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tooltip — follows mouse via onMouseMove on the table container */}
      <Tooltip tooltip={tooltip} x={tooltipPos.x} y={tooltipPos.y} />

      {/* Violations Panel */}
      {violationCount > 0 && (
        <div className="bg-card border border-red-500/20 rounded-sm p-4">
          <div className="text-xs tracking-widest uppercase text-red-400 font-bold mb-3 flex items-center gap-2">
            <AlertTriangle size={12} /> {violationCount} Compliance Violation{violationCount > 1 ? "s" : ""}
          </div>
          <div className="space-y-2">
            {violations.map(v => (
              <div key={v.id} className={`p-2 rounded-sm border-l-2 ${v.severity === "error" ? "border-red-500 bg-red-500/5" : "border-amber-500 bg-amber-500/5"}`}
                data-testid={`violation-${v.rule_code}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${v.severity === "error" ? "text-red-400" : "text-amber-400"}`}>{v.rule_code}</span>
                  <span className="text-xs text-foreground font-medium">{v.crew_name}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{v.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
